import type { PaginationDirection, PaginationOrder, PaginationSpec } from "@rpcbase/api"

import { PaginationValidationError } from "./errors"


const DISALLOWED_MONGO_FIELD_SEGMENTS = new Set(["__proto__", "constructor", "prototype"])
const PAGINATION_LIMIT_MAX = 128
export type NormalizedPaginationSpec = Omit<PaginationSpec, "direction" | "sort" | "limit"> & {
  direction: PaginationDirection;
  limit: number;
  sort: Array<{
    field: string;
    order: PaginationOrder;
  }>;
}

export const normalizePaginationSpec = (
  spec: PaginationSpec,
  options: { maxLimit?: number } = {},
): NormalizedPaginationSpec => {
  if (!spec || typeof spec !== "object") throw new PaginationValidationError("Invalid PaginationSpec")

  const limit = normalizeLimit(spec.limit, normalizeMaxLimit(options.maxLimit))
  const direction = spec.direction ?? "next"
  if (direction !== "next" && direction !== "prev") throw new PaginationValidationError("Invalid pagination direction")

  if (!Array.isArray(spec.sort) || spec.sort.length === 0) throw new PaginationValidationError("Invalid pagination sort")

  const sort: NormalizedPaginationSpec["sort"] = spec.sort.map(({ field, order }) => ({
    field: assertSafeMongoFieldPath(field),
    order: normalizeOrder(order),
  }))

  const seenFields = new Set<string>()
  for (const { field } of sort) {
    if (seenFields.has(field)) throw new PaginationValidationError(`Duplicate pagination sort field: ${field}`)
    seenFields.add(field)
  }

  const primaryOrder = sort[0]?.order

  if (!seenFields.has("_id")) {
    sort.push({ field: "_id", order: primaryOrder })
  }

  return {
    ...spec,
    limit,
    direction,
    sort,
  }
}

const normalizeMaxLimit = (maxLimit: unknown): number => {
  if (maxLimit === undefined) return PAGINATION_LIMIT_MAX
  if (!Number.isSafeInteger(maxLimit) || (maxLimit as number) <= 0) {
    throw new PaginationValidationError("Invalid pagination max limit")
  }
  return maxLimit as number
}

const normalizeLimit = (limit: unknown, maxLimit: number): number => {
  if (typeof limit !== "number" || !Number.isFinite(limit) || !Number.isInteger(limit) || limit <= 0) {
    throw new PaginationValidationError("Invalid pagination limit")
  }
  if (limit > maxLimit) {
    throw new PaginationValidationError("Invalid pagination limit")
  }
  return limit
}

const normalizeOrder = (order: unknown): PaginationOrder => {
  if (order !== "asc" && order !== "desc") throw new PaginationValidationError("Invalid pagination order")
  return order
}

const assertSafeMongoFieldPath = (field: unknown): string => {
  if (typeof field !== "string" || field.length === 0) throw new PaginationValidationError("Invalid pagination sort field")
  if (field.startsWith("$")) throw new PaginationValidationError("Invalid pagination sort field")

  const parts = field.split(".")
  for (const part of parts) {
    if (part.length === 0) throw new PaginationValidationError("Invalid pagination sort field")
    if (DISALLOWED_MONGO_FIELD_SEGMENTS.has(part)) throw new PaginationValidationError("Invalid pagination sort field")
    if (!/^[a-zA-Z0-9_]+$/.test(part)) throw new PaginationValidationError("Invalid pagination sort field")
  }

  return field
}
